import React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as Logo } from "../../../assets/images/logo.svg";
import useLoginForm from "../../../Hooks/u1/useLoginForm";
import { validateEmail } from "../../../utils/Helper";
import SessionConflictModal from "../../../components/common/SessionConflictModal";
import { Button } from "../../../components/common/u1/Button";
import { Input } from "../../../components/common/u1/Input";
import { PasswordInput } from "../../../components/common/u1/PasswordInput";
import { ErrorNote } from "../../../components/common/u1/ErrorNote";
import { AuthHeader } from "../../../components/common/u1/AuthHeader";
import { Navbar } from "../../../components/common/u1/Navbar";
import { ArrowRight } from "../../../assets/ui/icons";
import "../../../components/common/u1/tokens.css";

/* u1 Login screen.
 *
 * Visual uplift only. Every rule — validation, the request payload, TIP-511's
 * pinned-email guards, the redirect precedence, the analytics events — lives in
 * useLoginForm, transcribed from pages/public/Login.jsx rather than rewritten.
 * This file decides layout and nothing else, so the sign-in flow cannot drift
 * from the page it replaces.
 *
 * `modalLogin` / `preventRoute` are carried through unchanged: the guest gate
 * on a company card renders this inside a modal, where the panel chrome (logo,
 * footer, card surface) is suppressed and the email is pinned to the session
 * being re-authenticated.
 */
export default function Login({ modalLogin = false, preventRoute = false }) {
  const {
    formData,
    handleChange,
    error,
    isLoading,
    isPasswordVisible,
    togglePasswordVisibility,
    lockedEmail,
    handleLogin,
    handleSwitchAccount,
    showSessionPopup,
    sessionConflict,
    setShowSessionPopup,
    handleForgotPassword,
    handleCreateNewAccount,
  } = useLoginForm({ modalLogin, preventRoute });

  /* The submit stays disabled until the email looks valid and the password has something in it — no inline error message, the button simply stays off. */
  const canSubmit =
    Boolean(validateEmail(String(formData.email || "").trim())) &&
    Boolean(String(formData.password || "").trim()) &&
    !isLoading;

  const form = (
    <form onSubmit={handleLogin} noValidate={false} className="w-full">
      {!modalLogin && (
        <AuthHeader
          title="Welcome back"
          subtitle="Log in to manage your business on TREVEX."
        />
      )}

      <div className={`grid gap-3.5 ${modalLogin ? "mt-0" : "mt-7"}`}>
        <Input
          id="email"
          name="email"
          type="email"
          size="lg"
          label="Email"
          autoComplete="email"
          placeholder="you@company.com"
          onChange={handleChange}
          required
          /* TIP-511 — pinned to the account being re-authenticated. The value is
             still submitted because handleLogin reads formData, not the form's
             own field collection. */
          {...(lockedEmail
            ? {
                value: lockedEmail,
                disabled: true,
                "aria-disabled": true,
                title:
                  "You are signing back in to this account. Log out to use a different one.",
                className:
                  "bg-[var(--slate-2)] cursor-not-allowed text-[var(--muted)] disabled:opacity-100",
              }
            : {})}
        />

        {lockedEmail && (
          <p className="-mt-1 text-[12px] text-[var(--muted)] font-sans text-left">
            Signing back in to this account.{" "}
            <button
              type="button"
              onClick={handleSwitchAccount}
              className="text-[var(--navy)] font-bold underline"
            >
              Not you? Log out
            </button>
          </p>
        )}

        <PasswordInput
          id="password"
          name="password"
          size="lg"
          label="Password"
          autoComplete="current-password"
          placeholder="Your password"
          onChange={handleChange}
          required
          visible={isPasswordVisible}
          onToggleVisibility={togglePasswordVisibility}
          labelRight={
            !modalLogin && (
              <Link
                to="/reset-password"
                onClick={handleForgotPassword}
                className="text-[12.5px] font-semibold text-[var(--orange)] hover:underline font-sans"
              >
                Forgot?
              </Link>
            )
          }
        />
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Button
        type="submit"
        disabled={!canSubmit}
        className="w-full mt-5 h-[46px]"
      >
        {isLoading ? "Logging in…" : "Log in"}
        {!isLoading && <ArrowRight size={14} />}
      </Button>

      <p className="mt-5 text-center text-[13px] text-[var(--ink-2)] font-sans">
        New to TREVEX?{" "}
        <Link
          to="/sign-up"
          onClick={handleCreateNewAccount}
          className="text-[var(--orange)] font-semibold hover:underline"
        >
          Create an account
        </Link>
      </p>
    </form>
  );

  /* Inside the guest-gate modal the surrounding card, logo and footer belong to
     the modal, so only the form is returned. */
  if (modalLogin) {
    return (
      <div className="u1-root w-full text-left">
        {showSessionPopup && (
          <SessionConflictModal
            setShowSessionPopup={setShowSessionPopup}
            handleForceLogin={(e) => handleLogin(e, true)}
            conflict={sessionConflict}
          />
        )}
        {form}
      </div>
    );
  }

  return (
    <div className="u1-root min-h-screen w-full bg-[var(--bg)] flex flex-col">
      {showSessionPopup && (
        <SessionConflictModal
          setShowSessionPopup={setShowSessionPopup}
          handleForceLogin={(e) => handleLogin(e, true)}
        />
      )}

      {/* Only the one call to action: a Login link would point at this page. */}
      <Navbar
        actions={
          <Link to="/sign-up" onClick={handleCreateNewAccount}>
            <Button variant="primary" className="whitespace-nowrap">
              List your business <ArrowRight size={14} />
            </Button>
          </Link>
        }
      />

      {/* Amber wash behind the top of the card, as in the design. Decorative. */}
      <main className="relative flex-1 flex items-start justify-center px-3 sm:px-4 md:px-6 pt-6 sm:pt-8 md:pt-16 pb-11 sm:pb-14 md:pb-24">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[720px] max-w-full h-[360px] bg-[radial-gradient(ellipse_at_center_top,rgba(245,157,21,.10),transparent_60%)]"
        />

        <div className="relative w-full max-w-[480px] overflow-hidden rounded-2xl md:rounded-[18px] border border-[var(--line)] bg-white shadow-[0_24px_60px_-20px_rgba(0,47,73,.18),0_6px_18px_-6px_rgba(0,47,73,.08)]">
          {/* Brand bar — the logo sits inside the card, above the heading. */}
          <div className="flex items-center justify-center pt-5 px-[18px] sm:pt-[22px] sm:px-[22px] md:pt-[26px] md:px-9 bg-[linear-gradient(180deg,rgba(245,157,21,.05),transparent)]">
            <Link to="/" aria-label="TREVEX home" className="flex items-center">
              <Logo className="h-7 w-auto sm:h-9 md:h-10" />
            </Link>
          </div>

          <div className="px-[18px] pt-5 pb-6 sm:px-[22px] sm:pt-6 sm:pb-[26px] md:p-9">{form}</div>
        </div>
      </main>

    </div>
  );
}
